/**
 * Chute Feed Monitoring Page
 * ─────────────────────────────────────────────────────────────────────────────
 * QC enters periodic chute-feed samples during blowroom run.
 * System computes deviation from laydown plan target and alerts if drift
 * exceeds tolerance (MARGINAL → ALERT → CRITICAL).
 *
 * Sprint 0071:
 *   • Active Laydown dropdown now uses /chute-feed/active-laydowns which
 *     filters by the active-state set (RELEASED / CONDITIONING /
 *     READY_FOR_MIX / MIXING_IN_PROGRESS) so completed plans don't show.
 *   • All 12 HVI parameters are recorded (was only Mic / Rd / +b /
 *     Strength) so chute-vs-laydown comparison matches the bale HVI
 *     schema 1:1.
 *   • Upload HVI Report — operator can snap a photo of the lab printout
 *     or attach the PDF and the OCR pipeline pre-fills the form.
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Form, Input, InputNumber, Row,
  Select, Space, Table, Tag, Typography, Statistic, Upload,
  Divider,
} from 'antd'
import {
  PlusOutlined, AlertOutlined, FileImageOutlined, LoadingOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chuteFeedApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'
import PageHeader from '@/shared/components/PageHeader'
import FormDrawer from '@/shared/components/FormDrawer'
import dayjs from 'dayjs'

const { Text } = Typography

const STATUS_COLORS: Record<string, string> = {
  OK: 'green', MARGINAL: 'orange', ALERT: 'gold', CRITICAL: 'red',
}

const ACTION_OPTIONS = [
  { label: 'No action needed', value: 'No action needed' },
  { label: 'Operator informed', value: 'Operator informed' },
  { label: 'Bale arrangement adjusted', value: 'Bale arrangement adjusted' },
  { label: 'Laydown stopped for review', value: 'Laydown stopped for review' },
  { label: 'Emergency replacement', value: 'Emergency replacement' },
]

const DevCell = ({ val }: { val: any }) => {
  if (val === null || val === undefined) return <Text type="secondary">—</Text>
  const n = Number(val)
  return (
    <Text type={Math.abs(n) > 0.3 ? 'danger' : n !== 0 ? 'warning' : 'success'}>
      {n >= 0 ? `+${n.toFixed(2)}` : n.toFixed(2)}
    </Text>
  )
}

/** Read a File into a base64 string suitable for the parse-hvi endpoint. */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // result is "data:image/jpeg;base64,XXXX" — strip the prefix.
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.substring(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default function ChuteFeedPage() {
  const { message } = App.useApp()
  const qclient = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [laydownFilter, setLaydownFilter] = useState<string | undefined>()
  const [drawerPlantId, setDrawerPlantId] = useState<string | undefined>()
  // OCR state for the photo upload button.
  const [ocrLoading, setOcrLoading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null)
  const [ocrConfidence, setOcrConfidence] = useState<number | null>(null)

  const { data: tests = [], isLoading } = useQuery({
    queryKey: ['chute-feed', plantFilter, laydownFilter],
    queryFn: () => chuteFeedApi.list({
      plant_id: plantFilter,
      laydown_plan_id: laydownFilter,
      limit: 300,
    }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  // Sprint 0071 — list-page filter still uses any-plant active laydowns
  // so the Laydown dropdown above the table works without a plant pick.
  const { data: laydownsList = [] } = useQuery({
    queryKey: ['chute-active-laydowns-list', plantFilter],
    queryFn: () => chuteFeedApi.activeLaydowns(plantFilter),
  })

  // Drawer's own dropdown — driven by the plant the user picks INSIDE
  // the drawer so it works even when the list filter is empty.
  const { data: laydownsForDrawer = [] } = useQuery({
    queryKey: ['chute-active-laydowns-drawer', drawerPlantId],
    queryFn: () => chuteFeedApi.activeLaydowns(drawerPlantId),
    enabled: !!drawerPlantId && drawerOpen,
  })

  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []
  const fmtLaydownLabel = (l: any) => {
    const status = l.status ? ` [${l.status.replace(/_/g, ' ')}]` : ''
    return `${l.laydown_no} — ${l.count_description || 'no count'}${status}`
  }
  const laydownListOptions = (Array.isArray(laydownsList) ? laydownsList : [])
    .map((l: any) => ({ label: fmtLaydownLabel(l), value: l.id }))
  const laydownDrawerOptions = (Array.isArray(laydownsForDrawer) ? laydownsForDrawer : [])
    .map((l: any) => ({ label: fmtLaydownLabel(l), value: l.id }))

  const addMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => chuteFeedApi.add(data),
    onSuccess: () => {
      qclient.invalidateQueries({ queryKey: ['chute-feed'] })
      message.success('Sample recorded')
      setDrawerOpen(false)
      setUploadedFile(null)
      setOcrConfidence(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const handleHviUpload = async (file: File) => {
    setOcrLoading(true)
    setUploadedFile({ uid: file.name, name: file.name, size: file.size, type: file.type } as UploadFile)
    try {
      const base64 = await readFileAsBase64(file)
      const result = await chuteFeedApi.parseHvi({
        file_name: file.name, file_data: base64,
      })
      const parsed = result?.parsed ?? {}
      setOcrConfidence(result?.ocr_confidence_pct ?? null)
      // Pre-fill the form, never clobber values the user already typed.
      const current = form.getFieldsValue()
      const merged: Record<string, unknown> = { ...current }
      for (const [k, v] of Object.entries(parsed)) {
        if (v !== null && v !== undefined && (current[k] === undefined || current[k] === null || current[k] === '')) {
          merged[k] = v
        }
      }
      // Stash provenance so add_chute_test can persist it.
      merged.source = 'PHOTO_OCR'
      merged.hvi_report_file_name = file.name
      merged.ocr_confidence_pct = result?.ocr_confidence_pct ?? null
      form.setFieldsValue(merged)
      const filledCount = Object.values(parsed).filter(v => v !== null && v !== undefined).length
      if (filledCount === 0) {
        message.warning(result?.warning || 'No HVI numbers detected — enter manually')
      } else {
        message.success(`OCR pre-filled ${filledCount} field(s) — verify before saving`)
      }
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'OCR failed — enter values manually')
    } finally {
      setOcrLoading(false)
    }
    // Stop AntD from auto-uploading; we handle it ourselves.
    return false
  }

  const testList = Array.isArray(tests) ? tests : []
  const criticals = testList.filter((t: any) => t.result_status === 'CRITICAL').length
  const alerts = testList.filter((t: any) => t.result_status === 'ALERT').length

  const columns = [
    { title: 'Sample #', dataIndex: 'sample_seq', key: 'seq', width: 75, align: 'right' as const,
      fixed: 'left' as const },
    { title: 'Time', dataIndex: 'sample_time', key: 'time', width: 130,
      fixed: 'left' as const,
      render: (v: string) => dayjs(v).format('DD-MMM HH:mm') },
    { title: 'Mic', dataIndex: 'micronaire', key: 'mic', width: 65, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(2) : '—' },
    { title: 'Mic Δ', dataIndex: 'mic_dev', key: 'mic_dev', width: 75,
      render: (v: any) => <DevCell val={v} /> },
    { title: 'UHML', dataIndex: 'uhml_mm', key: 'uhml', width: 70, align: 'right' as const,
      render: (v: any) => v ? `${Number(v).toFixed(2)} mm` : '—' },
    { title: 'UI%', dataIndex: 'uniformity_index', key: 'ui', width: 65, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: 'Str', dataIndex: 'strength_g_tex', key: 'str', width: 65, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: 'Elong%', dataIndex: 'elongation_pct', key: 'elong', width: 75, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: 'SFI', dataIndex: 'sfi', key: 'sfi', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: 'Rd', dataIndex: 'rd_value', key: 'rd', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: 'Rd Δ', dataIndex: 'rd_dev', key: 'rd_dev', width: 70,
      render: (v: any) => <DevCell val={v} /> },
    { title: '+b', dataIndex: 'plus_b', key: 'pb', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : '—' },
    { title: '+b Δ', dataIndex: 'plus_b_dev', key: 'pb_dev', width: 70,
      render: (v: any) => <DevCell val={v} /> },
    { title: 'Trash%', dataIndex: 'trash_pct', key: 'tr', width: 75, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(2) : '—' },
    { title: 'Moist%', dataIndex: 'moisture_pct', key: 'm', width: 70, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(2) : '—' },
    { title: 'SCI', dataIndex: 'sci', key: 'sci', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(0) : '—' },
    { title: 'Source', dataIndex: 'source', key: 'src', width: 90,
      render: (v: string) => v && v !== 'MANUAL'
        ? <Tag color="cyan" style={{ fontSize: 10 }}>{v}</Tag>
        : <Text type="secondary" style={{ fontSize: 11 }}>manual</Text> },
    { title: 'Status', dataIndex: 'result_status', key: 'status', width: 90,
      fixed: 'right' as const,
      render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
  ]

  return (
    <div>
      <PageHeader
        title="Chute Feed Monitor"
        subtitle="Periodic quality check during blowroom run — detects mix drift vs laydown plan"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields()
              setUploadedFile(null)
              setOcrConfidence(null)
              setDrawerPlantId(plantFilter)
              if (plantFilter) form.setFieldsValue({ plant_id: plantFilter })
              setDrawerOpen(true)
            }}
          >
            Add Sample
          </Button>
        }
      />

      {/* Alerts */}
      {criticals > 0 && (
        <Alert type="error" showIcon icon={<AlertOutlined />} style={{ marginBottom: 12 }}
          message={`${criticals} CRITICAL deviation(s) detected — immediate action required`} />
      )}
      {alerts > 0 && criticals === 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${alerts} ALERT level deviation(s) — monitor closely`} />
      )}

      {/* Summary */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total Samples" value={testList.length} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="OK" value={testList.filter((t: any) => t.result_status === 'OK').length}
              valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Alert / Marginal"
              value={testList.filter((t: any) => ['ALERT', 'MARGINAL'].includes(t.result_status)).length}
              valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Critical" value={criticals} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <Select allowClear placeholder="Plant" options={plantOptions}
            onChange={v => setPlantFilter(v)} style={{ width: 160 }} />
        </Col>
        <Col>
          <Select allowClear placeholder="Active laydown" options={laydownListOptions}
            onChange={v => setLaydownFilter(v)} showSearch optionFilterProp="label" style={{ width: 320 }} />
        </Col>
      </Row>

      <Card>
        <Table rowKey="id" columns={columns} dataSource={testList} loading={isLoading}
          size="small" pagination={{ pageSize: 100 }} scroll={{ x: 'max-content' }}
          rowClassName={(r: any) =>
            r.result_status === 'CRITICAL' ? 'ant-table-row-danger' :
            r.result_status === 'ALERT' ? 'ant-table-row-warning' : ''
          }
        />
      </Card>

      <FormDrawer
        title="New Chute Feed Sample"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => addMutation.mutate(v))}
        loading={addMutation.isPending}
        form={form}
        width={560}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select
            options={plantOptions}
            onChange={(v) => {
              setDrawerPlantId(v)
              // clear selected laydown if plant switches
              form.setFieldsValue({ laydown_plan_id: undefined })
            }}
          />
        </Form.Item>

        <Form.Item
          name="laydown_plan_id"
          label="Active Laydown"
          rules={[{ required: true }]}
          tooltip="Lists laydowns in RELEASED / CONDITIONING / READY FOR MIX / MIXING IN PROGRESS state. If empty, the plant has no live laydown — start one in Laydown Planning."
        >
          <Select
            options={laydownDrawerOptions}
            showSearch
            optionFilterProp="label"
            placeholder={
              !drawerPlantId
                ? 'Select plant first'
                : laydownDrawerOptions.length === 0
                  ? 'No active laydowns for this plant'
                  : 'Select laydown'
            }
            notFoundContent={
              !drawerPlantId
                ? 'Pick a plant to load laydowns'
                : 'No active laydowns'
            }
          />
        </Form.Item>

        <Form.Item name="sample_seq" label="Sample #" initialValue={1}>
          <InputNumber style={{ width: '100%' }} min={1} />
        </Form.Item>

        {/* Sprint 0071 — Photo / file upload. Snap a photo of the lab
            printout, attach a PDF, or drop in an Excel/CSV. The OCR
            pipeline parses it server-side and pre-fills the form. */}
        <Card
          size="small"
          style={{ marginBottom: 16, background: '#f0f9ff', borderColor: '#91caff' }}
          styles={{ body: { padding: 12 } }}
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 13 }}>
              <FileImageOutlined /> Upload HVI Report (optional)
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Photo of lab printout · PDF · Excel · CSV. We OCR it and fill
              the values below. Verify before saving — correct any OCR mistakes.
            </Text>
            <Space wrap>
              <Upload
                accept=".jpg,.jpeg,.png,.bmp,.tiff,.webp,.pdf,.xlsx,.xls,.csv"
                showUploadList={false}
                beforeUpload={handleHviUpload}
                disabled={ocrLoading}
              >
                <Button icon={ocrLoading ? <LoadingOutlined /> : <FileImageOutlined />}
                  loading={ocrLoading} disabled={ocrLoading}>
                  {ocrLoading ? 'Parsing…' : 'Upload HVI Report'}
                </Button>
              </Upload>
              {uploadedFile && (
                <Tag color={ocrConfidence !== null && ocrConfidence >= 75 ? 'green' :
                            ocrConfidence !== null && ocrConfidence >= 40 ? 'orange' : 'red'}>
                  {uploadedFile.name}
                  {ocrConfidence !== null ? ` · ${ocrConfidence.toFixed(0)}% parsed` : ''}
                </Tag>
              )}
            </Space>
          </Space>
        </Card>

        <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 12 }}>
          Length / Strength
        </Divider>
        <Row gutter={12}>
          <Col xs={12} md={8}>
            <Form.Item name="micronaire" label="Micronaire">
              <InputNumber style={{ width: '100%' }} step={0.01} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="uhml_mm" label="UHML (mm)" tooltip="Upper Half Mean Length">
              <InputNumber style={{ width: '100%' }} step={0.01} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="uniformity_index" label="Uniformity %">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="strength_g_tex" label="Strength (g/tex)">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="elongation_pct" label="Elongation %">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="sfi" label="SFI" tooltip="Short Fiber Index">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 12 }}>
          Color / Trash / Moisture
        </Divider>
        <Row gutter={12}>
          <Col xs={12} md={8}>
            <Form.Item name="rd_value" label="Rd (Reflectance)">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="plus_b" label="+b (Yellowness)">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="color_grade" label="Color Grade" tooltip="USDA / CCI grade e.g. 31-3">
              <Input placeholder="e.g. 31-3" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="trash_pct" label="Trash %">
              <InputNumber style={{ width: '100%' }} step={0.01} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="moisture_pct" label="Moisture %">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="sci" label="SCI" tooltip="Spinning Consistency Index">
              <InputNumber style={{ width: '100%' }} step={1} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 12 }}>
          Action / Comments
        </Divider>
        <Form.Item name="action_taken" label="Action Taken">
          <Select allowClear options={ACTION_OPTIONS} />
        </Form.Item>
        <Form.Item name="comments" label="Comments">
          <Input.TextArea rows={2} maxLength={300} placeholder="Optional — context for QC review" />
        </Form.Item>

        {/* Hidden fields for OCR provenance — populated by handleHviUpload */}
        <Form.Item name="source" hidden initialValue="MANUAL"><Input /></Form.Item>
        <Form.Item name="hvi_report_file_name" hidden><Input /></Form.Item>
        <Form.Item name="ocr_confidence_pct" hidden><InputNumber /></Form.Item>
      </FormDrawer>
    </div>
  )
}

